import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import PropTypes from 'prop-types';
import Loader from "../../components/widgets/Loader.jsx";
import useCustomGetStoreState from "../../hooks/useCustomGetStoreState.jsx";
import { useStoreActions, useStoreState } from "easy-peasy";
import useBackButton from "../../hooks/useBackButton.jsx";
import { useModal } from "../../hooks/useModal.jsx";
import AuthorizationChecker from "../../services/AuthorizationChecker.js";
import UploadFileFormComponent, { submitFileForm } from "../../components/widgets/uploadFileForm/UploadFileFormComponent.jsx";
import EditableImageComponent from "../../components/widgets/editableImage/EditableImageComponent.jsx";
import HelperMetiers360 from "../../services/HelpersMetiers360.js";
import VideosPageContent from "../video/VideosPageContent.jsx";
import RomesFilters from "./RomesFilters.jsx";
import RomesView from "./RomesView.jsx";
import RomesTableComponent from "../../components/romes/RomesTableComponent.jsx";
import SelectItemsComponent from "../../components/widgets/selectItemsComponent/SelectItemsComponent.jsx";
import ProfessionalsPageContent from "./ProfessionalsPageContent.jsx";
import './TransversalPropDetailPage.scss';
import CustomTabsWithParams from "../../components/widgets/customTabs/CustomTabsWithParams.jsx";

const TransversalPropDetailPage = ({ transversalProp, displayProsTab = true, displayRomesTabForEveryone = false,
    displayProfessionalFieldsFilter = false }) => {
    const { apiData: { BACKOFFICE_DEFAULT_VALUES } } = useStoreState(state => state.actionSlugs);
    const { fileContraints } = BACKOFFICE_DEFAULT_VALUES;
    const { uniqueId } = useParams();
    const { isFetchingOneTransversalProp } = useCustomGetStoreState('romes');
    const { fetchOneTransversalProp, updateOneTransversalPropThumbnail } = useStoreActions(actions => actions.romes);
    const { oneTransversalProp } = useStoreState(state => state.romes);
    const { allVideos, isFetchingAllVideos } = useCustomGetStoreState("videos");
    const { backButtonComponent } = useBackButton();
    const { modalComponent, setModalShow, modalData, setModalData, setIsSending } = useModal();
    const { apiData: { ACTIONS } } = useStoreState(state => state.actionSlugs);
    const { isMobile } = useStoreState(state => state.responsive);

    const viewStyleRomesPreference = useStoreState(state => state.preferences.codeRomesListContent);

    const [filteredRomes, setFilteredRomes] = useState([]);
    const [viewStyleRomes, setViewStyleRomes] = useState(viewStyleRomesPreference);

    const professionalFieldRef = useRef(null);
    const [searchParamsAppliedOnView, setSearchParamsAppliedOnView] = useState(false);

    const viewStyleRomesType = [
        {
            value: 'withContentM360', label: "Avec contenus M360", view: <RomesTableComponent romes={filteredRomes.filter(rome =>
                rome?.usagePros !== 0 || rome?.usageVideos !== 0 || rome?.usageStories !== 0
            )} />
        },
        { value: 'all', label: "Tous", view: <RomesTableComponent romes={filteredRomes} /> },
    ];

    const displayRomesTab = displayRomesTabForEveryone || AuthorizationChecker.isAdmin();

    const [searchParam, setSearchParam] = useSearchParams();

    const [romesForProfessionalField, setRomesForProfessionalField] = useState([]);
    const [videosForProfessionalField, setVideosForProfessionalField] = useState([]);

    const videos = useMemo(() => {
        return allVideos?.filter(video =>
            (oneTransversalProp?.videosByPro ?? oneTransversalProp?.videosByRome)?.includes(video.uniqueId))
    }, [allVideos, oneTransversalProp]);

    const professionalFields = useMemo(() => {
        return displayProfessionalFieldsFilter && oneTransversalProp
            ? [... new Map(oneTransversalProp.romes?.map(rome => [rome.professionalField?.code, rome.professionalField])).values()]
            : []
    }, [oneTransversalProp]);

    useLayoutEffect(() => {
        if (professionalFieldRef.current && searchParam?.get('professionalField')) {
            professionalFieldRef.current.value = searchParam.get('professionalField');
        }
        if (videos) {
            setVideosForProfessionalField(videos);
            if (professionalFieldRef?.current?.value) {
                onFilterSlected();
            }
        }
    }, [searchParam, videos]);

    useEffect(() => {
        if (!isFetchingOneTransversalProp && !isFetchingAllVideos) {
            fetchOneTransversalProp({
                code: uniqueId,
                transversalProp
            })
        }
    }, [allVideos, uniqueId]);

    useEffect(() => {
        if (oneTransversalProp?.romes) {
            searchParam?.get('professionalField')
                ? !searchParamsAppliedOnView && applySelectProfessionalFieldOnTables()
                : setRomesForProfessionalField(oneTransversalProp?.romes);
        }
    }, [oneTransversalProp])

    const updateThumbnailModal = () => {
        if (AuthorizationChecker.hasUpdateRights('romes')) {

            const validate = () => {
                setIsSending(true);
                return submitFileForm(uniqueId, updateOneTransversalPropThumbnail, transversalProp + "_updateThumbnail", fileContraints, "image", transversalProp)
                    .then(() => {
                        setModalShow(false);
                    })
                    .finally(() => setIsSending(false));
            }
            setModalData({
                ...modalData,
                header: <>Modifier la vignette</>,
                content: <UploadFileFormComponent typeOfFile="image" fileFormName={transversalProp + "_updateThumbnail"} fileLabel="Vignette" token={ACTIONS[transversalProp]["updateThumbnail"]["csrfToken"]} />,
                cancelButton: 'Annuler',
                validateButton: "Valider",
                onValidate: validate,
                size: 'lg'
            });
            setModalShow(true);
        }
    }

    const applySelectProfessionalFieldOnTables = () => {
        if (oneTransversalProp) {
            setRomesForProfessionalField(professionalFieldRef.current?.value
                ? oneTransversalProp?.romes?.filter(rome =>
                    rome.professionalField?.code === professionalFieldRef.current?.value)
                : oneTransversalProp?.romes);

            setVideosForProfessionalField(professionalFieldRef.current?.value
                ? videos.filter(video => video.romes?.some(rome => rome.includes(professionalFieldRef.current?.value)))
                : videos);
            !searchParamsAppliedOnView && setSearchParamsAppliedOnView(true);
        }
    }

    const onFilterSlected = () => {
        let params = {};
        if (professionalFieldRef.current?.value)
            params.professionalField = professionalFieldRef.current?.value
        if (searchParam?.get('tab'))
            params.tab = searchParam?.get('tab')

        setSearchParam({ ...params, ...{ "professionalField": professionalFieldRef.current?.value } });
        applySelectProfessionalFieldOnTables()
    }

    const contentTabs = [
        {
            eventKey: "videos",
            title: "Expériences immersives associées",
            mountOnEnter: true,
            unmountOnExit: true,
            content: oneTransversalProp?.videosByPro || oneTransversalProp?.videosByRome
                ? <>
                    <VideosPageContent videos={videosForProfessionalField} isVideosLoading={isFetchingAllVideos} />
                </>
                : <Loader />
        }
    ];
    if (displayProsTab) {
        contentTabs.splice(0, 0, {
            eventKey: "professionnals",
            title: "Professionnel⋅les associé⋅es",
            mountOnEnter: true,
            unmountOnExit: true,
            content: oneTransversalProp?.pros
                ? <>
                    <ProfessionalsPageContent
                        professionals={oneTransversalProp?.pros ?? []}
                        isFetching={isFetchingOneTransversalProp}
                    />
                </>
                : <Loader />
        })
    }
    if (displayRomesTab) {
        contentTabs.push({
            eventKey: "romeCodes",
            title: "Codes ROME associés",
            mountOnEnter: true,
            unmountOnExit: true,
            content: romesForProfessionalField
                ? <>
                    <RomesFilters
                        romes={romesForProfessionalField}
                        setFilteredRomes={setFilteredRomes}
                        viewStyle={viewStyleRomes}
                        setViewStyle={setViewStyleRomes}
                        viewStyleType={viewStyleRomesType} />
                    <Row>
                        <RomesView
                            romes={romesForProfessionalField}
                            isFetching={isFetchingOneTransversalProp}
                            filteredRomes={filteredRomes}
                            view={viewStyleRomesType?.find(type => type.value === viewStyleRomes)?.view} />
                    </Row>
                </>
                : <Loader />
        })
    }

    return (
        <Container fluid>
            <Row className="item-page">
                <h2 className={"d-flex " + (isMobile ? "flex-column-reverse" : "justify-content-between")}>
                    {isFetchingOneTransversalProp ? <Loader /> : <>
                        <Col className={"d-flex " + (isMobile ? "flex-column" : "justify-content-between")}>
                            {oneTransversalProp?.thumbnail &&
                                <Col className={isMobile ? "d-flex justify-content-center" : "col-auto"}>
                                    <EditableImageComponent src={oneTransversalProp.thumbnail} onEdit={updateThumbnailModal} isEditable={AuthorizationChecker.hasUpdateRights('romes')} />
                                </Col>
                            }
                            <Col>{HelperMetiers360.transversalPropLabel(transversalProp) + " : " + oneTransversalProp?.label}</Col>
                        </Col>
                        <Col className={isMobile ? "d-flex justify-content-end" : "col-auto"}>
                            {backButtonComponent}
                        </Col>
                    </>
                    }
                </h2>
            </Row>
            {isFetchingOneTransversalProp ?
                <Loader />
                : <>
                    {displayProfessionalFieldsFilter && professionalFields.length > 0 &&
                        <div className={"d-flex flex-row pt-4 pe-5 select-professional-field"}>
                            <label className="label-select text-nowrap">Sous-domaine professionnel&nbsp;: </label>
                            <span className="d-flex flex-column w-100">
                                <SelectItemsComponent selectRef={professionalFieldRef} onChange={onFilterSlected}
                                    items={professionalFields} itemKey="code" itemValue="label" itemValueDetails="code" />
                            </span>
                        </div>}
                    <CustomTabsWithParams
                        contentTabs={contentTabs}
                        defaultActiveTabId={displayProsTab ? "professionnals" : "videos"}
                        id="transversal-prop-tabs"
                    />
                </>}
            {modalComponent}
        </Container>
    );
}

TransversalPropDetailPage.propTypes = {
    transversalProp: PropTypes.string.isRequired,
    displayProsTab: PropTypes.bool,
    displayRomesTabForEveryone: PropTypes.bool,
    displayProfessionalFieldsFilter: PropTypes.bool,
};

export default TransversalPropDetailPage;